import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { MdArrowBack, MdDoneAll, MdPlayArrow } from "react-icons/md";
import axios from "../config/axiosInstance";
import { CRM } from "../config/apiConfig";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
} from "../components/crm/Primitives";
import {
  SkeletonHeader,
  SkeletonKpiStrip,
  SkeletonWorkList,
} from "../components/crm/Skeletons";
import WorkRow from "../components/crm/WorkRow";
import AccountDrawer from "../components/crm/AccountDrawer";
import { formatMoney, formatNumber } from "../components/crm/crmHelpers";

/*
  Uma fila de trabalho inteira.

  A regra, a ordem e o `highlight` (o número que justifica cada conta estar
  aqui) vêm do servidor — `admin/services/crm/workQueueService.js`. Esta tela
  não filtra nem reordena nada: se filtrasse, a contagem do chip em "Hoje" e a
  lista daqui discordariam, e a equipe pararia de confiar nas duas.

  A versão anterior era uma tabela de oito colunas fixas. Numa fila de
  conversão de free, isso significava sete linhas de "Mensalidade R$ 0,00" e
  sete de "Vencimento" (irrelevante para upsell), com o dado que importa —
  agendamentos no mês — espremido no meio. Agora cada linha mostra o motivo
  dela, e "trabalhar a lista" abre o painel na primeira conta e segue de uma
  para a outra sem fechar.
*/

const QueuePage = () => {
  const { id } = useParams();
  const [openId, setOpenId] = useState(null);

  const queueQuery = useQuery(["crm", "queue", id], async () => {
    const { data } = await axios.get(`${CRM.QUEUE}/${id}`);
    return data.data;
  });

  if (queueQuery.isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonHeader />
        <SkeletonKpiStrip count={3} withIcon={false} />
        <SkeletonWorkList rows={8} withHeader={false} />
      </div>
    );
  }

  if (queueQuery.error) {
    return (
      <ErrorBlock
        message={
          queueQuery.error.response?.data?.message ||
          "Não foi possível carregar esta fila."
        }
        onRetry={queueQuery.refetch}
      />
    );
  }

  const queue = queueQuery.data;
  const rows = queue.rows || [];
  const openIndex = rows.findIndex((row) => row.id === openId);

  return (
    <div className="space-y-4">
      <header className="flex items-start gap-3">
        <Link
          to="/"
          className="mt-1 shrink-0 text-slate-400 transition hover:text-pink-600"
          title="Voltar"
        >
          <MdArrowBack size={20} />
        </Link>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-slate-900">{queue.label}</h1>
            <Badge tone={queue.tone}>{formatNumber(queue.count)}</Badge>
            {queue.valueAtStake > 0 ? (
              <span className="text-sm font-semibold text-amber-700">
                {formatMoney(queue.valueAtStake)}/mês em jogo
              </span>
            ) : null}
          </div>
          <p className="text-sm text-slate-500">{queue.why}</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800">
            {queue.action}
          </p>
          {queue.snoozed > 0 ? (
            <p className="mt-0.5 text-xs text-slate-500">
              {formatNumber(queue.snoozed)} conta(s) se encaixam na regra mas
              saíram da fila: já receberam contato
              {queue.cooldownDays > 0
                ? ` nos últimos ${queue.cooldownDays} dia(s)`
                : ""}{" "}
              ou têm tarefa aberta.
            </p>
          ) : null}
        </div>

        {rows.length ? (
          <button
            type="button"
            onClick={() => setOpenId(rows[0].id)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-slate-900 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-slate-700"
          >
            <MdPlayArrow size={16} /> trabalhar a lista
          </button>
        ) : null}
      </header>

      {queue.count === 0 ? (
        <Card>
          <EmptyState
            icon={MdDoneAll}
            title="Fila zerada"
            description={
              queue.snoozed > 0
                ? `${queue.snoozed} conta(s) se encaixam na regra, mas já receberam contato recente ou têm tarefa aberta. Não há nada novo para fazer aqui.`
                : "Nenhuma conta se encaixa nesta regra agora."
            }
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {rows.map((row) => (
            <WorkRow
              key={row.id}
              row={row}
              highlight={queue.highlight}
              queueId={queue.id}
              isActive={openId === row.id}
              onOpen={setOpenId}
            />
          ))}

          {queue.hasMore ? (
            <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
              Mostrando as primeiras {formatNumber(rows.length)} de{" "}
              {formatNumber(queue.count)} — trabalhe estas primeiro, a fila
              recalcula sozinha.
            </p>
          ) : null}
        </Card>
      )}

      {openId ? (
        <AccountDrawer
          companyId={openId}
          onClose={() => setOpenId(null)}
          position={openIndex >= 0 ? openIndex + 1 : null}
          total={rows.length}
          onPrev={
            openIndex > 0 ? () => setOpenId(rows[openIndex - 1].id) : undefined
          }
          onNext={
            openIndex >= 0 && openIndex < rows.length - 1
              ? () => setOpenId(rows[openIndex + 1].id)
              : undefined
          }
        />
      ) : null}
    </div>
  );
};

export default QueuePage;
